// Copyright (c) 2024-present, Dinocollab Technologies, Inc. and its affiliates. All rights reserved.

// imports
import { Box, styled } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DataSurfaceViewMode, LoadingModeRule, DataSurfaceFeatureMode } from './types'
import { createViewList, createViewListLoading } from './view-list'
import { createViewGrid, createViewGridLoading } from './view-grid'
import { createViewSwitchTransition } from './view-switch-transition'
import { PanelInfiniteScrollFooter, PanelLoadMore, PanelLoading, PanelNoData, PanelPaginationFooter } from './ui.units'
// types
import type { BoxProps } from '@mui/material'
import type { ComponentType, FC } from 'react'
import type { IViewGridLoadingProps } from './view-grid'
import type { IViewGridConfig, IViewGridProps } from './view-grid/types'
import type { TPagination, TDataSurfaceState, TRenderableNode } from './types'
import type { IViewListConfig, IViewListLoadingProps, IViewListProps } from './view-list'

export interface IDataSurfaceSlots<T> {
  listViewProps?: IViewListProps<T>
  gridViewProps?: IViewGridProps<T>
  /** Custom loading component. If not provided, a default loading component will be used. */
  PanelLoading?: ComponentType<{ viewMode: DataSurfaceViewMode }>
  /** Custom no data component. If not provided, a default no data component will be used. */
  PanelNoData?: ComponentType<{ viewMode: DataSurfaceViewMode }>
  /** Custom load more component for infinite scroll mode. If not provided, a default loading indicator will be shown when fetching the next page. */
  PanelLoadMore?: ComponentType
  footer?: {
    status?: TRenderableNode
    range?: TRenderableNode
  }
}

export interface IDataSurfaceProps<T> {
  value?: T[]
  /**
   * @deprecated No longer used. Use `viewMode` and `loadMode` props directly instead.
   * Controlled state. When provided, DataSurface becomes fully controlled.
   */
  state?: TDataSurfaceState
  /**
   * @deprecated No longer used. Component now uses uncontrolled mode with props.
   * Called on any state change (viewMode, loadMode, page, pageSize).
   */
  onStateChange?: (state: TDataSurfaceState) => void
  viewMode?: DataSurfaceViewMode
  /** Pagination metadata shared by both modes. */
  pagination?: TPagination
  /** Default page size. @default 20 */
  defaultPageSize?: number
  /** True while a page fetch is in flight (shows loading indicator). */
  loading?: boolean
  /**
   * Called when the component needs a new page of data.
   * - pagination mode: triggered by user clicking page control
   * - infiniteScroll mode: triggered automatically when scrolling near the bottom
   * Supports async operations - return a Promise to indicate loading state.
   */
  onPageChange?: (page: number, pageSize: number) => void | Promise<void>
  /** Load orchestration mode. @default 'pagination' */
  loadMode?: LoadingModeRule
  sx?: BoxProps['sx']
  slots?: IDataSurfaceSlots<T>
  /** Enable row selection with checkboxes (List view only). */
  selectable?: boolean
  /** Array of selected row IDs (List view only). */
  selectedIds?: (string | number)[]
  /** Callback when selection changes (List view only). */
  onSelectionChange?: (selectedIds: (string | number)[]) => void
}

export interface IDataSurfaceConfig<T> {
  /** Feature mode determines the internal optimizations and rendering strategies. @default 'client' */
  featureMode?: DataSurfaceFeatureMode
  /** Default view mode when not provided via props. @default 'grid' */
  defaultViewMode?: DataSurfaceViewMode
  /** Default load mode when not provided via props. @default 'pagination' */
  defaultLoadMode?: LoadingModeRule
  /** Default page size. @default 20 */
  defaultPageSize?: number
  /** Configuration for List view. If not provided, List view will use default settings. */
  listConfig?: IViewListConfig<T>
  /** Custom List component. If not provided, a default List component will be used. */
  ListComponent?: ComponentType<IViewListProps<T>>
  /** Configuration for Grid view. If not provided, Grid view will use default settings. */
  gridConfig?: IViewGridConfig<T>
  /** Custom Grid component. If not provided, a default Grid component will be used. */
  GridComponent?: ComponentType<IViewGridProps<T>>
  /** Custom loading component. If not provided, a default loading component will be used. */
  PanelLoading?: ComponentType<{ viewMode: DataSurfaceViewMode }>
  /** Custom no data component. If not provided, a default no data component will be used. */
  PanelNoData?: ComponentType<{ viewMode: DataSurfaceViewMode }>
  /** Custom load more component for infinite scroll mode. If not provided, a default loading indicator will be shown when fetching the next page. */
  PanelLoadMore?: ComponentType
}

export function createDataSurface<T>(config: IDataSurfaceConfig<T>) {
  if (!config.GridComponent) {
    const configInput: IViewGridConfig<T> = config.gridConfig || { getterId: (item: T, index: number) => index }
    config.gridConfig = configInput
    config.GridComponent = createViewGrid<T>(configInput)
  }

  if (!config.ListComponent) {
    const configInput: IViewListConfig<T> = config.listConfig || { getterId: (item: T, index: number) => index }
    config.listConfig = configInput
    config.ListComponent = createViewList<T>(configInput)
  }

  const { defaultViewMode = 'grid', defaultLoadMode = 'pagination', featureMode = 'client' } = config
  const ViewSwitchTranstionInstance = createViewSwitchTransition<DataSurfaceViewMode>({})
  const GridLoadingComponent = createViewGridLoading<T>(config.GridComponent as ComponentType<IViewGridProps<T>>)
  const ListLoadingComponent = createViewListLoading<T>(config.ListComponent as ComponentType<IViewListProps<T>>)

  const DataSurface: FC<IDataSurfaceProps<T>> = (props) => {
    const slots = props.slots || {}
    const defaultPageSize = props.pagination?.pageSize ?? config.defaultPageSize ?? 20
    // separate pagination state
    const [internalPagination, setInternalPagination] = useState<TPagination>({ page: 0, pageSize: defaultPageSize })

    // resolved pagination: external takes priority over internal
    const paginationState = useMemo<TPagination>(() => {
      const p = props.pagination ?? internalPagination
      return {
        page: p.page ?? 0,
        pageSize: p.pageSize ?? defaultPageSize,
        hasNext: p.hasNext ?? false,
        hasPrev: p.hasPrev ?? false,
        total: p.total
      }
    }, [props.pagination, internalPagination])

    const viewMode = props.viewMode ?? defaultViewMode
    const loadMode = props.loadMode ?? defaultLoadMode

    // Determine renderStrategy based on current viewMode
    const renderStrategy = useMemo(() => {
      if (viewMode === 'grid') {
        return config.gridConfig?.renderStrategy ?? 'normal'
      }
      return config.listConfig?.renderStrategy ?? 'normal'
    }, [viewMode])

    const handlePrevPage = useCallback(async () => {
      const currentPage = paginationState.page ?? 0
      if (currentPage <= 0) return
      const prevPage = currentPage - 1
      if (!props.pagination) {
        setInternalPagination((prev) => ({ ...prev, page: prevPage }))
      }
      await props.onPageChange?.(prevPage, paginationState.pageSize ?? defaultPageSize)
    }, [paginationState.page, paginationState.pageSize, props.pagination, props.onPageChange])

    const handleNextPage = useCallback(async () => {
      if (!paginationState.hasNext) return
      const currentPage = paginationState.page ?? 0
      const nextPage = currentPage + 1
      if (!props.pagination) {
        setInternalPagination((prev) => ({ ...prev, page: nextPage }))
      }
      await props.onPageChange?.(nextPage, paginationState.pageSize ?? defaultPageSize)
    }, [paginationState.hasNext, paginationState.page, paginationState.pageSize, props.pagination, props.onPageChange])

    const handlePageJump = useCallback(
      async (targetPage: number) => {
        if (targetPage < 0) return
        if (!props.pagination) {
          setInternalPagination((prev) => ({ ...prev, page: targetPage }))
        }
        await props.onPageChange?.(targetPage, paginationState.pageSize ?? defaultPageSize)
      },
      [paginationState.pageSize, props.pagination, props.onPageChange]
    )

    const finalValue = props.value || []

    // Token changes whenever props.value reference changes — used to reset scroll in views (client mode only)
    const scrollResetToken = featureMode === 'client' ? props.value : undefined

    // Reset page to 0 when the data source changes (e.g. new filtered array passed in) — client mode only
    useEffect(() => {
      if (featureMode === 'client' && !props.pagination) {
        setInternalPagination((prev) => ({ ...prev, page: 0 }))
      }
    }, [props.value])

    // Server mode, no total: infer hasNext=false when a load completes without adding new items
    const prevLoadLengthRef = useRef<number | null>(null)
    const [inferredHasNoMore, setInferredHasNoMore] = useState(false)

    useEffect(() => {
      if (featureMode !== 'server' || props.pagination?.total !== undefined) return
      if (props.loading) {
        prevLoadLengthRef.current = finalValue.length
      } else if (prevLoadLengthRef.current !== null) {
        if (finalValue.length <= prevLoadLengthRef.current) {
          setInferredHasNoMore(true)
        } else {
          setInferredHasNoMore(false)
        }
        prevLoadLengthRef.current = null
      }
    }, [props.loading, featureMode, props.pagination?.total, finalValue.length])

    // Reset inferred state when going back to page 0 (e.g. filter change)
    useEffect(() => {
      if (paginationState.page === 0) setInferredHasNoMore(false)
    }, [paginationState.page])

    // When running in client feature mode, derive pagination metadata from the full value
    const derivedPagination = useMemo<TPagination>(() => {
      const page = paginationState.page ?? 0
      const pageSize = paginationState.pageSize ?? defaultPageSize
      if (featureMode === 'client') {
        const total = finalValue.length
        const hasNext = pageSize * (page + 1) < total
        const hasPrev = page > 0
        return { page, pageSize, hasNext, hasPrev, total }
      }
      // Server mode without total: override hasNext when we've inferred no more items
      if (paginationState.total === undefined && inferredHasNoMore) {
        return { ...paginationState, hasNext: false }
      }
      return paginationState
    }, [paginationState, finalValue.length, featureMode, defaultPageSize, inferredHasNoMore])

    // Slice the input value when client-side pagination is enabled
    const paginatedValue = useMemo(() => {
      if (featureMode === 'client') {
        const page = derivedPagination.page ?? 0
        const pageSize = derivedPagination.pageSize ?? defaultPageSize
        if (loadMode === 'infiniteScroll') {
          // accumulate all items from page 0 → current page
          return finalValue.slice(0, (page + 1) * pageSize)
        }
        const start = page * pageSize
        return finalValue.slice(start, start + pageSize)
      }
      return finalValue
    }, [finalValue, derivedPagination, featureMode, loadMode, defaultPageSize])

    const isEmptyData = finalValue.length === 0
    const isInitialLoading = Boolean(props.loading && isEmptyData)
    const isNoData = !props.loading && isEmptyData
    const showLoadingOverlay = Boolean(props.loading) && !isEmptyData

    const handleGridPageChange = useCallback(
      async (page: number, pageSize: number) => {
        if (!props.pagination) {
          setInternalPagination({ page, pageSize })
        }
        await props.onPageChange?.(page, pageSize)
      },
      [props.pagination, props.onPageChange]
    )

    const gridPayload = useMemo<IViewGridLoadingProps<T>>(
      () => ({
        value: paginatedValue,
        loadMode,
        loading: props.loading,
        pagination: derivedPagination,
        onPageChange: handleGridPageChange,
        scrollResetToken,
        ...slots.gridViewProps
      }),
      [paginatedValue, loadMode, props.loading, derivedPagination, handleGridPageChange, scrollResetToken, slots.gridViewProps]
    )

    const gridPayloadRef = useRef<IViewGridLoadingProps<T>>(gridPayload)
    gridPayloadRef.current = gridPayload

    const listPayload = useMemo<IViewListLoadingProps<T>>(
      () => ({
        value: paginatedValue,
        loadMode,
        loading: props.loading,
        pagination: derivedPagination,
        onPageChange: handleGridPageChange,
        renderStrategy: renderStrategy,
        selectable: props.selectable ?? config.listConfig?.selectable,
        selectedIds: props.selectedIds,
        onSelectionChange: props.onSelectionChange,
        scrollResetToken,
        ...slots.listViewProps
      }),
      [
        paginatedValue,
        loadMode,
        props.loading,
        derivedPagination,
        renderStrategy,
        handleGridPageChange,
        props.selectable,
        props.selectedIds,
        props.onSelectionChange,
        scrollResetToken,
        slots.listViewProps
      ]
    )

    const listPayloadRef = useRef<IViewListLoadingProps<T>>(listPayload)
    listPayloadRef.current = listPayload

    const ListViewContent = useMemo<ComponentType<{ value: DataSurfaceViewMode }>>(() => {
      const ViewListContent: FC<{ value: DataSurfaceViewMode }> = () => <ListLoadingComponent {...listPayloadRef.current} />
      return ViewListContent
    }, [ListLoadingComponent])

    const GridViewContent = useMemo<ComponentType<{ value: DataSurfaceViewMode }>>(() => {
      const ViewGridContent: FC<{ value: DataSurfaceViewMode }> = () => <GridLoadingComponent {...gridPayloadRef.current} />
      return ViewGridContent
    }, [GridLoadingComponent])

    const PanelLoadingComponent = useMemo(() => {
      return slots.PanelLoading || config?.PanelLoading || PanelLoading
    }, [slots.PanelLoading, config.PanelLoading, viewMode])

    const PanelNoDataComponent = useMemo(() => {
      return slots.PanelNoData || config?.PanelNoData || PanelNoData
    }, [slots.PanelNoData, config.PanelNoData, viewMode])

    const PanelLoadMoreComponent = useMemo(() => {
      return slots.PanelLoadMore || config?.PanelLoadMore || PanelLoadMore
    }, [slots.PanelLoadMore, config.PanelLoadMore])

    const renderFooter = () => {
      if (loadMode === LoadingModeRule.infiniteScroll) {
        const footerSlots = props.slots?.footer
        return (
          <PanelInfiniteScrollFooter
            loading={props.loading}
            hasNext={derivedPagination.hasNext}
            currentCount={paginatedValue.length}
            total={derivedPagination.total}
            slots={{
              statusText: footerSlots?.status,
              rangeText: footerSlots?.range
            }}
          />
        )
      }

      // pagination mode
      return (
        <PanelPaginationFooter
          page={derivedPagination.page ?? 0}
          pageSize={derivedPagination.pageSize ?? defaultPageSize}
          total={derivedPagination.total}
          hasNext={derivedPagination.hasNext}
          hasPrev={(derivedPagination.page ?? 0) > 0}
          loading={props.loading}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
          onPageJump={handlePageJump}
        />
      )
    }

    return (
      <DataSurfaceStyled sx={props.sx}>
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {isInitialLoading && <PanelLoadingComponent viewMode={viewMode} />}
          {isNoData && <PanelNoDataComponent viewMode={viewMode} />}
          {!isInitialLoading && !isNoData && (
            <ViewSwitchTranstionInstance
              value={viewMode}
              viewA={{ value: 'list', Content: ListViewContent }}
              viewB={{ value: 'grid', Content: GridViewContent }}
            />
          )}
          {showLoadingOverlay && <PanelLoadMoreComponent />}
        </Box>
        {renderFooter()}
      </DataSurfaceStyled>
    )
  }
  return DataSurface
}

export default createDataSurface

const DataSurfaceStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  position: 'relative',
  flex: 1,
  minHeight: 0,
  backgroundColor: theme.palette.background.default
}))
